import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Check, X, Flag, RotateCcw, Award, RefreshCw } from "lucide-react";
import { api, apiError, authHeader, getToken, clearToken } from "@/lib/api";
import { Logo } from "@/components/Logo";

const FILTERS = ["all", "pending", "approved", "rejected", "waitlisted", "badge_activated", "flagged"];

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <p className="text-xs text-white/50">{label}</p>
    <p className={`mt-1 font-display text-2xl font-semibold ${accent || "text-white"}`}>{value}</p>
  </div>
);

const Badge = ({ children, tone }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>{children}</span>
);

const statusTone = (r) => ({
  pending: "bg-amber-400/15 text-amber-300",
  approved: "bg-indril-green/15 text-indril-green",
  rejected: "bg-red-500/15 text-red-300",
  waitlisted: "bg-indril-blue/15 text-indril-blue",
  duplicate: "bg-white/10 text-white/60",
  spam: "bg-white/10 text-white/60",
}[r] || "bg-white/10 text-white/60");

export default function FounderDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/founders/stats", { headers: authHeader() }),
        api.get("/admin/founders", { params: { q, status, limit: 50 }, headers: authHeader() }),
      ]);
      setStats(s.data); setItems(l.data.items); setTotal(l.data.total);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/bossadmin/login"); return; }
      toast.error(apiError(err));
    } finally { setLoading(false); }
  }, [q, status, nav]);

  useEffect(() => { if (!getToken()) { nav("/bossadmin/login"); return; } load(); }, [load, nav]);

  const act = async (id, path, body) => {
    try {
      await api.post(`/admin/founders/${id}/${path}`, body || {}, { headers: authHeader() });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const activateBadges = async () => {
    if (!window.confirm("Activate Founder Creator badges for ALL eligible approved users? This is a one-time batch action.")) return;
    try {
      const { data } = await api.post("/admin/founders/activate-badges", {}, { headers: authHeader() });
      toast.success(`Activated ${data.activated} badges`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const logout = () => { clearToken(); nav("/bossadmin/login"); };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-zinc-950/80 px-5 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-3"><Logo markClass="h-8 w-8" /><span className="text-sm text-white/50">Founder Creators</span></div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="rounded-full border border-white/10 p-2 hover:bg-white/5" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4" /></button>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        {stats && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              <StatCard label="Total" value={stats.total} />
              <StatCard label="Pending" value={stats.pending} accent="text-amber-300" />
              <StatCard label="Approved" value={stats.approved_waiting_milestone} accent="text-indril-green" />
              <StatCard label="Rejected" value={stats.rejected} accent="text-red-300" />
              <StatCard label="Waitlisted" value={stats.waitlisted} accent="text-indril-blue" />
              <StatCard label="Badges" value={stats.badge_activated} accent="text-indril-saffron" />
              <StatCard label="Today" value={stats.today} />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm text-white/60">
                Remaining to 10,000 milestone: <span className="font-semibold text-white">{stats.remaining_to_milestone.toLocaleString()}</span>
                {stats.milestone_reached && <span className="ml-2 text-indril-green">Milestone reached ✓</span>}
              </p>
              <button onClick={activateBadges} disabled={!stats.milestone_reached}
                className="inline-flex items-center gap-2 rounded-full bg-indril-saffron px-4 py-2 text-sm font-semibold text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40" data-testid="activate-badges-btn">
                <Award className="h-4 w-4" /> Activate Badges
              </button>
            </div>
          </>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search name, mobile, email, user ID" className="w-full rounded-full border border-white/10 bg-white/5 py-2.5 pl-9 pr-4 text-sm outline-none focus:border-indril-saffron/60" data-testid="admin-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setStatus(f)} data-testid={`filter-${f}`}
                className={`rounded-full px-3 py-1.5 text-xs capitalize ${status === f ? "bg-white text-zinc-950" : "border border-white/10 text-white/60 hover:bg-white/5"}`}>
                {f.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="founders-table">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-wide text-white/40">
              <tr>
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">User ID</th><th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Mobile</th><th className="px-4 py-3">Seq</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Badge</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-white/40">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-white/40">No registrations found.</td></tr>}
              {!loading && items.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid="founder-row">
                  <td className="px-4 py-3 font-medium">{r.full_name}</td>
                  <td className="px-4 py-3 text-white/60">@{r.normalized_user_id}</td>
                  <td className="px-4 py-3 text-white/60">{r.normalized_email}</td>
                  <td className="px-4 py-3 text-white/60">{r.normalized_mobile}</td>
                  <td className="px-4 py-3 text-white/50">{r.eligibility_sequence ?? "—"}</td>
                  <td className="px-4 py-3"><Badge tone={statusTone(r.review_status)}>{r.review_status}</Badge></td>
                  <td className="px-4 py-3">{r.badge_status === "activated" ? <Badge tone="bg-indril-saffron/15 text-indril-saffron">activated</Badge> : <span className="text-white/40">{r.badge_status}</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => act(r.id, "approve")} title="Approve" className="rounded-lg p-1.5 text-indril-green hover:bg-white/5" data-testid="approve-btn"><Check className="h-4 w-4" /></button>
                      <button onClick={() => { const reason = window.prompt("Rejection reason?") ?? ""; act(r.id, "reject", { reason }); }} title="Reject" className="rounded-lg p-1.5 text-red-300 hover:bg-white/5" data-testid="reject-btn"><X className="h-4 w-4" /></button>
                      <button onClick={() => act(r.id, "flag", { reason: "duplicate" })} title="Flag duplicate" className="rounded-lg p-1.5 text-white/50 hover:bg-white/5"><Flag className="h-4 w-4" /></button>
                      <button onClick={() => act(r.id, "restore")} title="Restore" className="rounded-lg p-1.5 text-indril-blue hover:bg-white/5"><RotateCcw className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-white/40">{total} registration(s)</p>
      </main>
    </div>
  );
}
